import type { DisplayDetail, Schedule } from "@led-signage/shared/management-api";
import { useState, type ReactNode } from "react";
import { newIdempotencyKey } from "./api/client.js";
import { describeFailure, type ActionFailure } from "./api/errors.js";
import { AuthenticationRecoveryButton } from "./AuthenticationRecoveryButton.js";
import { api, usingFixtures } from "./api/index.js";
import { useResource, type Resource } from "./api/useResource.js";
import { PermissionNotice, useCan } from "./session.js";
import {
  emptyEmergencyForm,
  inspectEmergency,
  toEmergencyRequest,
  type EmergencyFormState,
} from "./emergency.js";

function Gate<T>({
  resource,
  children,
}: {
  resource: Resource<T>;
  children: (value: T) => ReactNode;
}) {
  if (resource.state === "loading") {
    return <p className="resource-note">読み込み中…</p>;
  }
  if (resource.state === "error") {
    return (
      <div className="inline-alert">
        <b>読み込めませんでした</b>
        <span>{resource.message}</span>
      </div>
    );
  }
  return <>{children(resource.value)}</>;
}

type SubmitState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "done"; requestId: string }
  | { kind: "failed"; failure: ActionFailure };

/** 編成の選択肢。スケジュールごとに最新Revisionを1つ出す。 */
function ScheduleOptions({ schedule }: { schedule: Schedule }) {
  const detail = useResource(
    (signal) => api.getSchedule(schedule.id, signal),
    [schedule.id],
  );
  if (detail.state !== "ready") return null;
  const latest = detail.value.latestRevision;
  if (latest === null) return null;
  return (
    <option value={latest.id}>
      {schedule.name} — Revision {latest.revisionNumber}（優先度 {latest.priority}）
    </option>
  );
}

export function EmergencySection() {
  const displays = useResource((signal) => api.listDisplays(signal), []);
  const schedules = useResource(
    (signal) => api.listSchedules(undefined, signal),
    [],
  );
  const [form, setForm] = useState<EmergencyFormState>(emptyEmergencyForm);
  const [submit, setSubmit] = useState<SubmitState>({ kind: "idle" });

  const allowed = useCan("emergency:override");
  const issues = inspectEmergency(form, new Date());
  const blocked = issues.length > 0 || !allowed;

  async function request() {
    setSubmit({ kind: "running" });
    try {
      const created = await api.createPublicationRequest(
        toEmergencyRequest(form),
        newIdempotencyKey(),
      );
      // 緊急依頼はsubmitで承認済みになる。承認者を待たない。
      await api.submitPublicationRequest(created.id, newIdempotencyKey());
      setSubmit({ kind: "done", requestId: created.id });
      setForm(emptyEmergencyForm());
    } catch (error) {
      setSubmit({ kind: "failed", failure: describeFailure(error) });
    }
  }

  return (
    <>
      <header className="section-heading">
        <div>
          <span className="eyebrow">RESTRICTED OPERATION</span>
          <h2>緊急配信</h2>
          <p>通常スケジュールへ一時的に割り込み、指定した編成を優先再生します。</p>
        </div>
      </header>

      <div className="emergency-warning">
        <span>!</span>
        <div>
          <b>この操作は承認を省略します</b>
          {/*
            通常の公開は別の承認者を必要とするが、緊急配信は申請した時点で
            承認済みになる（approval-and-publishing.md「緊急配信」）。
            「二者承認後に開始」と書くと事実と違う。
          */}
          <p>
            緊急配信は申請した時点で承認済みになり、別の承認者は確認しません。
            理由と自動終了日時が監査ログへ残ります。配信を端末へ届けるには、
            このあと管理者が「公開する」を実行する必要があります。
          </p>
        </div>
      </div>

      <PermissionNotice permission="emergency:override" action="緊急配信の申請" />

      {usingFixtures && (
        <div className="resource-note resource-note--fixture">
          表示中のデータはローカル確認用のサンプルです。申請は実APIでのみ実行できます。
        </div>
      )}

      <div className="emergency-layout">
        <section className="panel emergency-form">
          <h2>緊急配信を申請</h2>

          <label className="field">
            <span>緊急配信する編成</span>
            <Gate resource={schedules}>
              {(page) => (
                <select
                  value={form.scheduleRevisionId}
                  onChange={(event) => {
                    setForm((current) => ({
                      ...current,
                      scheduleRevisionId: event.target.value,
                    }));
                  }}
                >
                  <option value="">選択してください</option>
                  {page.items.map((schedule) => (
                    <ScheduleOptions key={schedule.id} schedule={schedule} />
                  ))}
                </select>
              )}
            </Gate>
          </label>

          <div className="field">
            <span>対象のLED</span>
            <Gate resource={displays}>
              {(page) => (
                <div className="display-picker">
                  {page.items.map((item: DisplayDetail) => (
                    <label key={item.display.id}>
                      <input
                        type="checkbox"
                        checked={form.displayIds.includes(item.display.id)}
                        onChange={() => {
                          setForm((current) => ({
                            ...current,
                            displayIds: current.displayIds.includes(item.display.id)
                              ? current.displayIds.filter(
                                  (id) => id !== item.display.id,
                                )
                              : [...current.displayIds, item.display.id],
                          }));
                        }}
                      />
                      {item.display.name}
                    </label>
                  ))}
                </div>
              )}
            </Gate>
          </div>

          <label className="field">
            <span>自動終了日時</span>
            <input
              type="datetime-local"
              value={form.endsAtLocal}
              onChange={(event) => {
                setForm((current) => ({
                  ...current,
                  endsAtLocal: event.target.value,
                }));
              }}
            />
          </label>

          <label className="field">
            <span>緊急配信が必要な理由</span>
            <textarea
              rows={4}
              value={form.reason}
              placeholder="理由は監査ログへ記録されます"
              onChange={(event) => {
                setForm((current) => ({ ...current, reason: event.target.value }));
              }}
            />
          </label>

          <label className="confirm-line">
            <input
              type="checkbox"
              checked={form.acknowledged}
              onChange={(event) => {
                setForm((current) => ({
                  ...current,
                  acknowledged: event.target.checked,
                }));
              }}
            />
            通常スケジュールが中断されることを確認しました
          </label>

          {issues.length > 0 && (
            <ul className="form-issues">
              {issues.map((issue) => (
                <li key={issue.field} className="form-issue form-issue--error">
                  {issue.message}
                </li>
              ))}
            </ul>
          )}

          {submit.kind === "failed" && (
            <div className="inline-alert inline-alert--error">
              <b>申請できませんでした</b>
              <span>{submit.failure.message}</span>
              <AuthenticationRecoveryButton failure={submit.failure} />
              {submit.failure.code === "PERMISSION_DENIED" && (
                <span>
                  緊急配信の申請には緊急配信責任者の権限が必要です。管理者へ依頼してください。
                </span>
              )}
            </div>
          )}

          {submit.kind === "done" && (
            <div className="inline-alert inline-alert--ok">
              <b>申請しました（承認済み）</b>
              <span>申請 {submit.requestId}</span>
              <span>
                端末へ届けるには、承認待ち画面から管理者が「公開する」を実行してください。
              </span>
            </div>
          )}

          <button
            className="danger-button"
            type="button"
            disabled={blocked || submit.kind === "running"}
            onClick={() => void request()}
          >
            {submit.kind === "running" ? "申請中…" : "内容を確認して緊急配信を申請"}
          </button>
        </section>

        <aside className="panel safety-checklist">
          <h2>開始前チェック</h2>
          <ul>
            <li>現地担当者へ連絡した</li>
            <li>編成の内容と音声設定を確認した</li>
            <li>対象のLEDを再確認した</li>
            <li>自動終了日時を設定した</li>
            <li>通常放映への復帰方法を確認した</li>
          </ul>
          <p>
            緊急配信は承認を省略しますが、公開操作は省略しません。申請後に管理者が
            公開して初めて端末へ届きます。
          </p>
        </aside>
      </div>
    </>
  );
}
